import CloseIcon from '@mui/icons-material/CloseRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import ZoomInIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutIcon from '@mui/icons-material/ZoomOutRounded';
import { Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { directUrlOf, type DocumentSource } from './documentSource';
import { PdfPage } from './PdfPage';
import { PdfViewerBar } from './PdfViewerBar';
import { usePdfDocument } from './usePdfDocument';

interface PdfViewerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Where the PDF's bytes come from. Fetched and rendered here, so it does not
   *  matter whether the file may be embedded or is served as an attachment —
   *  see `usePdfDocument`. */
  source: DocumentSource;
  /** Names the document in the header bar and to a screen reader. */
  title: string;
}

/** Fit-to-width is 1. The range exists for a director enlarging small print,
 *  not for a design tool — past 3x a phone is scrolling in two axes, which is
 *  the experience this viewer was built to remove. */
const minZoom = 1;
const maxZoom = 3;
const zoomStep = 0.5;

/** Gutter around the page inside the scroller, in CSS pixels, matching the
 *  `p-3` the scroller is padded with. Subtracted from the measured width so a
 *  fitted page sits inside its padding rather than under it. */
const pageGutter = 24;

/** The viewer no longer takes the whole window: 90% of it on a phone, 80% from
 *  `sm` up, on both axes.
 *
 *  This overrides the theme's own `MuiDialog` paper, which pins a dialog
 *  full-bleed below `sm` — right for a form, where the alternative is a
 *  margined modal wasting a third of the screen, but this one is a reader, and
 *  a sliver of the page behind it is what tells a director the guide is
 *  something they are looking at rather than somewhere they have been sent.
 *  `!` is not needed for the same reason nothing else here uses it: MUI emits
 *  into `@layer mui`, which every Tailwind utility outranks (§5). */
const DIALOG_PAPER_CLASS =
  'm-0 h-9/10 max-h-none w-9/10 max-w-none rounded-2xl sm:h-4/5 sm:w-4/5';

/** The shell fills whatever the paper turned out to be, rather than the window.
 *  `h-dvh` would overflow a paper that is now smaller than the viewport at
 *  every width. */
const SHELL_CLASS = 'h-full';

/**
 * A PDF, read in the app rather than downloaded out of it.
 *
 * The document is rendered here, page by page onto canvases, rather than handed
 * to the browser inside an `<iframe>`. Framing is what the viewer used to do
 * and it does not work on the devices this portal is used on: mobile Safari
 * renders the first page of a framed PDF at native print size and gives the
 * frame no scrolling of its own, so the reader gets one page, magnified, with
 * no way forward. Every page here is scaled to the width of the screen and the
 * whole document scrolls as one column, which is the behaviour a phone reader
 * expects (§4).
 *
 * Sized to most of the window rather than all of it — 90% on a phone, 80% from
 * `sm` up. The reader keeps a sliver of the screen it was opened from at every
 * width, which is what says the guide is something being looked at rather than
 * somewhere the director has been sent; on a laptop it also leaves the work the
 * guide describes visible behind it.
 *
 * The document can also be kept. It is downloaded from the copy already parsed
 * rather than from `src`, which for a bucket URL is the difference between a
 * named file and a second request the browser will not let us name.
 */
export const PdfViewerDialog = ({ open, onClose, source, title }: PdfViewerDialogProps) => {
  // The route out of the viewer, which an id-sourced document may not have.
  const directUrl = directUrlOf(source);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [zoom, setZoom] = useState(minZoom);
  const [visiblePage, setVisiblePage] = useState(1);
  const pdf = usePdfDocument(source, open);

  // Reopening starts at the top, at fit-to-width: the reader came back to read
  // the guide again, not to resume someone else's zoom. Reset as part of
  // closing rather than in an effect watching `open` — the close IS the event,
  // and reacting to it afterwards only adds a second render.
  const handleClose = () => {
    setZoom(minZoom);
    setVisiblePage(1);
    onClose();
  };

  // The page width is measured rather than assumed, because it is the input the
  // fit-to-width scale is computed from — a hard-coded viewport width would be
  // wrong on every device with a notch, a split view or a scrollbar.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!open || scroller === null) return;

    // Falls back to the viewport when the container reports nothing, which it
    // does on the first paint before layout has run. Zero would render a
    // document with no pages in it — a blank reader is worse than one page
    // sized slightly wide for a frame that is about to be measured anyway.
    const measure = () => {
      const available = scroller.clientWidth || window.innerWidth;
      setPageWidth(Math.max(available - pageGutter, 0));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(scroller);

    return () => {
      observer.disconnect();
    };
  }, [open, pdf.status]);

  const pageCount = pdf.status === 'ready' ? pdf.document.numPages : 0;

  // Which page is being read, from the scroll position: a counter that only
  // updates on a tap would be wrong the moment the reader scrolls, and on a
  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (scroller === null || pageCount === 0) return;

    const progress = scroller.scrollTop / Math.max(scroller.scrollHeight, 1);
    setVisiblePage(Math.min(Math.floor(progress * pageCount) + 1, pageCount));
  };

  return (
    /* `fullScreen` is gone: the paper is sized directly at every width, and
       leaving the prop on would fight the classes that do it. `SHELL_CLASS`
       carries the matching height, since the paper only constrains the box. */
    <Dialog
      open={open}
      onClose={handleClose}
      // The label goes on the PAPER, not on the Dialog: MUI spreads a Dialog's
      // own props onto the outer `role="presentation"` wrapper, so `aria-label`
      // there never reaches the `role="dialog"` element and leaves it unnamed.
      slotProps={{ paper: { className: DIALOG_PAPER_CLASS, 'aria-label': title } }}
    >
      {/* `overflow-hidden` so the header and footer bars are clipped by the
          paper's rounded corners instead of squaring them off. */}
      <div className={`flex flex-col overflow-hidden bg-canvas ${SHELL_CLASS}`}>
        <header className="flex shrink-0 items-center gap-1 border-b border-divider bg-surface px-2 py-2">
          <Typography variant="subtitle2" className="min-w-0 flex-1 truncate pl-2">
            {title}
          </Typography>

          {pdf.status === 'ready' && (
            <>
              <IconButton
                aria-label="Zoom out"
                disabled={zoom <= minZoom}
                onClick={() => {
                  setZoom((current) => Math.max(current - zoomStep, minZoom));
                }}
              >
                <ZoomOutIcon />
              </IconButton>
              <IconButton
                aria-label="Zoom in"
                disabled={zoom >= maxZoom}
                onClick={() => {
                  setZoom((current) => Math.min(current + zoomStep, maxZoom));
                }}
              >
                <ZoomInIcon />
              </IconButton>
            </>
          )}

          <IconButton aria-label={`Close ${title}`} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </header>

        {pdf.status === 'loading' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3" role="status">
            <CircularProgress size={24} />
            <Typography variant="body2" className="text-content-muted">
              Loading {title}…
            </Typography>
          </div>
        )}

        {/* The document may sit on a host that refuses cross-origin reads, in
            which case there is nothing to render and the honest move is to say
            so and hand over the route that does work. */}
        {pdf.status === 'error' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <Typography variant="body2" className="text-content-muted" role="alert">
              {directUrl === ''
                ? 'This document can’t be opened right now. Try again in a moment.'
                : 'This document can’t be rendered here. Open it in a new tab to read it.'}
            </Typography>
            {directUrl !== '' && (
              <Button
                component="a"
                href={directUrl}
                target="_blank"
                rel="noreferrer"
                variant="contained"
                endIcon={<OpenInNewIcon />}
              >
                Open it in a new tab
              </Button>
            )}
          </div>
        )}

        {/* One scrolling column of pages. `overscroll-contain` keeps a flick at
            the end of the document from scrolling the page behind the dialog,
            which on iOS is what makes a full-screen reader feel loose. */}
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="min-h-0 flex-1 overflow-auto overscroll-contain p-3"
        >
          {pdf.status === 'ready' && pageWidth > 0 && (
            <div className="flex flex-col items-center gap-3">
              {Array.from({ length: pageCount }, (_, index) => (
                <PdfPage
                  key={index + 1}
                  document={pdf.document}
                  pageNumber={index + 1}
                  width={pageWidth}
                  zoom={zoom}
                />
              ))}
            </div>
          )}
        </div>

        {pdf.status === 'ready' && (
          <PdfViewerBar
            visiblePage={visiblePage}
            pageCount={pageCount}
            directUrl={directUrl}
            downloadUrl={pdf.downloadUrl}
            title={title}
          />
        )}
      </div>
    </Dialog>
  );
};
